export const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const INTERVALS = [15, 30, 45, 60] as const;

export const WEEK_ORDER: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

export const normalizeWeekdayLabel = (day: string) => {
  const key = String(day || '').trim().toLowerCase();
  const found = DAYS.find((d) => d.toLowerCase() === key);
  return found || String(day || '').trim();
};

export const normConsultationType = (value?: string | null): 'Online' | 'Offline' => {
  const raw = String(value || 'Offline').trim().toLowerCase();
  return raw === 'online' ? 'Online' : 'Offline';
};

export const sameBranchId = (a?: string | null, b?: string | null) =>
  a != null && b != null && String(a).trim() === String(b).trim();

export const parseTimeToMinutes = (value: string) => {
  if (!value) return 0;
  const raw = String(value).trim().toLowerCase();
  const isPM = raw.includes('pm');
  const isAM = raw.includes('am');
  const cleanTime = raw.replace(/[ap]m/g, '').trim();
  const [hStr, mStr] = cleanTime.split(':');
  let h = Number(hStr || 0);
  let m = Number(mStr || 0);
  if (isPM && h !== 12) h += 12;
  if (isAM && h === 12) h = 0;
  return h * 60 + m;
};

export const to12h = (value: string) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/[ap]m/i.test(raw)) {
    const match = raw.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (match) {
      const hh = String(match[1]).padStart(2, '0');
      return `${hh}:${match[2]} ${match[3].toUpperCase()}`;
    }
    return raw;
  }
  const [hStr, mStr] = raw.split(':');
  let h = Number(hStr || 0);
  const m = Number(mStr || 0);
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
};

export const from12hTo24h = (value: string) => {
  const mins = parseTimeToMinutes(value);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const generateSlots = (startTime: string, endTime: string, intervalMinutes: number) => {
  const start = parseTimeToMinutes(startTime);
  let end = parseTimeToMinutes(endTime);
  if (end === 0 && start > 720) end = 1440;
  const slots: string[] = [];
  for (let t = start; t < end; t += intervalMinutes) {
    const h24 = String(Math.floor(t / 60)).padStart(2, '0');
    const m = String(t % 60).padStart(2, '0');
    slots.push(to12h(`${h24}:${m}`));
  }
  return slots;
};

export const normSpecialityKey = (s?: string | null) => String(s || '').trim().toLowerCase();

export const templateSpecialityKey = (row: {
  specialityId?: string | null;
  speciality_id?: string | null;
  speciality?: string | null;
}) =>
  String(row?.specialityId || row?.speciality_id || '').trim() ||
  normSpecialityKey(row?.speciality);

export interface SpecialityTimeslotGroup<T extends { startTime: string }> {
  specialityId: string | null;
  speciality: string;
  templates: T[];
}

/** Group timeslot rows by speciality; sort groups and windows by start time. */
export const groupTemplatesBySpeciality = <
  T extends {
    specialityId?: string | null;
    speciality_id?: string | null;
    speciality?: string | null;
    startTime: string;
  },
>(
  templates: T[] = []
): SpecialityTimeslotGroup<T>[] => {
  const map = new Map<string, SpecialityTimeslotGroup<T>>();
  for (const row of templates) {
    const key = templateSpecialityKey(row);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        specialityId: row.specialityId || row.speciality_id || null,
        speciality: row.speciality || key,
        templates: [],
      });
    }
    map.get(key)!.templates.push(row);
  }
  const groups = Array.from(map.values());
  groups.forEach((group) => {
    group.templates.sort(
      (a, b) => parseTimeToMinutes(a.startTime) - parseTimeToMinutes(b.startTime)
    );
  });
  groups.sort(
    (a, b) =>
      parseTimeToMinutes(a.templates[0]?.startTime) -
      parseTimeToMinutes(b.templates[0]?.startTime)
  );
  return groups;
};

export interface SessionQueueItem {
  timeSlotID?: string;
  day: string;
  specialityId: string;
  speciality: string;
  startTime: string;
  endTime: string;
  intervalMinutes: number;
  isActive: boolean;
  consultationType?: 'Online' | 'Offline';
}

const sessionTimeWindow = (session: Pick<SessionQueueItem, 'startTime' | 'endTime'>) => {
  const startMins = parseTimeToMinutes(session.startTime);
  let endMins = parseTimeToMinutes(session.endTime);
  if (endMins === 0 && startMins > 720) endMins = 1440;
  return { startMins, endMins };
};

export const sessionsOverlapSameSpeciality = (
  a: SessionQueueItem,
  b: SessionQueueItem,
  sameDay = true
) => {
  if (sameDay && a.day && b.day && normalizeWeekdayLabel(a.day) !== normalizeWeekdayLabel(b.day)) {
    return false;
  }
  if (templateSpecialityKey(a) !== templateSpecialityKey(b)) return false;
  const wa = sessionTimeWindow(a);
  const wb = sessionTimeWindow(b);
  return wa.startMins < wb.endMins && wa.endMins > wb.startMins;
};

export const extractTimeslotApiError = (error: unknown, fallback = 'Failed to save timeslots') => {
  const err = error as { response?: { data?: Record<string, unknown> }; message?: string };
  const data = err?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  const failed = data?.failed;
  if (Array.isArray(failed) && failed.length > 0) {
    const first = failed.find((f) => (f as { message?: string })?.message) || failed[0];
    if ((first as { message?: string })?.message) return String((first as { message?: string }).message);
  }
  const conflicts = data?.conflicts;
  if (Array.isArray(conflicts) && conflicts.length > 0) {
    const c = conflicts[0] as { branchName?: string; day?: string };
    if (c?.branchName) {
      return `Timeslot overlaps with branch "${c.branchName}" on ${c.day || 'that day'}. A doctor cannot be scheduled at two branches at the same time.`;
    }
  }
  return err?.message || fallback;
};
